/* eslint-disable eqeqeq */
import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import "./attendee-login.css";
import ReCAPTCHA from "react-google-recaptcha";
import AttendeeLoginFormData from "../../../services/attendee-login/attendee-login-data";
import { getErrorMessage } from "../../../utils/validations";
import clock from "../../../assets/clock.svg";
import calendar from "../../../assets/calendar.svg";
import { formatDateTime, formatDuration } from "../../../utils/format";
import { useParams } from "react-router-dom";
import Loader from "../../../utils/loader/loader";
import { toast } from "react-toastify";
import LobbyPage from "../../../components/lobby/attendee-lobby";

const AttendeeLoginForm = ({ eventData }: { eventData: any }) => {
  const { eventId } = useParams<{ eventId: string }>();
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const sitekey = process.env.REACT_APP_RECAPTCHA_SITEKEY || "";
  const [dateTime, setDateTime] = useState({
    formattedDate: "",
    formattedTime: "",
    timeZone: "",
  });
  const eventDuration = formatDuration(eventData?.duration);
  const [loading, setLoading] = useState(false);
  const [sdkData, setSdkData] = useState<any>(() => {
    const storedSdkData = sessionStorage.getItem("sdkData");
    return storedSdkData ? JSON.parse(storedSdkData) : null;
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    return sessionStorage.getItem("isLoggedIn") === "true";
  }); // Track if the user is logged in

  useEffect(() => {
    if (eventData.date && eventData.timeZone) {
      const formattedDateTime = formatDateTime(
        eventData.date,
        eventData.timeZone
      );
      setDateTime(formattedDateTime);
    }

    // Check if email is stored in localStorage
    const storedEmail = localStorage.getItem("rememberedEmail");
    if (storedEmail) {
      setValue("email", storedEmail);
      setRememberMe(true);
    }
  }, [eventData.date, eventData.timeZone, setValue]);

  const onSubmit = async (formData: any) => {
    setLoading(true);

    // Verify reCAPTCHA before proceeding
    const recaptchaValue = recaptchaRef.current?.getValue();
    if (!recaptchaValue) {
      toast.error("Please complete the CAPTCHA to proceed.");
      setLoading(false);
      return;
    }

    // Handle Remember Me functionality
    if (rememberMe) {
      localStorage.setItem("rememberedEmail", formData.email);
    } else {
      localStorage.removeItem("rememberedEmail");
    }

    try {
      const numericEventId = Number(eventId);
      const response = await AttendeeLoginFormData(formData, numericEventId);
      if (response && response.status === 200) {
        const userName = response?.data?.data?.userName || "";
        const nameParts = userName.split(" ");
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";
        const responceData = {
          firstName,
          lastName,
          email: response?.data?.data?.userEmail,
          eventName: String(response?.data?.data?.eventName),
          eventId: Number(response?.data?.data?.meetingNumber),
        };
        sessionStorage.setItem("attendeeinfo", JSON.stringify(responceData));

        // Store login state, SDK data, and event data before breaking out of iframe
        if (window.top === window.self) {
          sessionStorage.setItem("isLoggedIn", "true");
          sessionStorage.setItem("sdkData", JSON.stringify(response.data.data));
          sessionStorage.setItem("eventData", JSON.stringify(eventData)); // Store eventData in session storage
        }
        setSdkData(response.data.data);
        setIsLoggedIn(true); // Set the logged-in state to true
        // Construct URL with parameters to pass data to top window
        const newUrl = `${window.location.href
          }?isLoggedIn=true&sdkData=${encodeURIComponent(
            JSON.stringify(response.data.data)
          )}&eventData=${encodeURIComponent(JSON.stringify(eventData))}`;
        // Break out of iframe if necessary
        if (window.top && window.top !== window.self) {
          window.top.location.href = newUrl; // Redirect top window to the URL with parameters
        }
      } else if (response && response.status === 404) {
        if (response.data.message === "You are not registered attendee") {
          toast.error("Click to Claim Your Spot and Register Now!");
        } else {
          toast.error(response.data.message);
        }
      } else if (response && response.status === 403) {
        toast.error(response.data.message);
      } else {
        console.error("Unexpected response:", response);
        throw new Error("API call failed");
      }
    } catch (error) {
      toast.error("Form submission failed!");
    } finally {
      setLoading(false);
    }
  };

  const handleRememberMeChange = () => {
    setRememberMe(!rememberMe);
  };

  // Check if the user is logged in and if data exists in session storage
  useEffect(() => {
    try {
      const storedSdkData = sessionStorage.getItem("sdkData");
      const storedEventData = sessionStorage.getItem("eventData");
      const loggedInState = sessionStorage.getItem("isLoggedIn");
      if (loggedInState === "true" && storedSdkData && storedEventData) {
        try {
          setSdkData(JSON.parse(storedSdkData));
          setIsLoggedIn(true);
        } catch (error) {
          console.error("Error parsing stored SDK data:", error);
        }
      }
      // Check URL parameters if session storage is empty
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has("isLoggedIn") && urlParams.get("isLoggedIn") === "true") {
        let sdkDataFromUrl = null;
        let eventDataFromUrl = null;
        // ✅ Safe Decoding Function
        const safeDecodeURIComponent = (value: string | null): string | null => {
          if (!value) return null; // If no value, return null
          try {
            let decodedValue = decodeURIComponent(value); // Try decoding once
            // 🔄 Try decoding again if it still contains encoded characters
            if (/%[0-9A-Fa-f]{2}/.test(decodedValue)) {
              decodedValue = decodeURIComponent(decodedValue);
            }
            return decodedValue;
          } catch (error) {
            console.warn("Decoding error, returning raw value:", value);
            return value; // Return raw value if decoding fails
          }
        };
        // ✅ Decode SDK Data Safely
        try {
          const rawSdkData = urlParams.get("sdkData");
          if (rawSdkData) {
            const decodedSdkData = safeDecodeURIComponent(rawSdkData);
            sdkDataFromUrl = JSON.parse(decodedSdkData!);
          }
        } catch (error) {
          console.error("Error decoding SDK data from URL:", error);
        }
        // ✅ Decode Event Data Safely
        try {
          const rawEventData = urlParams.get("eventData");
          if (rawEventData) {
            const decodedEventData = safeDecodeURIComponent(rawEventData);
            eventDataFromUrl = JSON.parse(decodedEventData!);
          }
        } catch (error) {
          console.error("Error decoding event data from URL:", error);
        }
        // Store the data in session storage only if valid
        if (sdkDataFromUrl) {
          sessionStorage.setItem("sdkData", JSON.stringify(sdkDataFromUrl));
          setSdkData(sdkDataFromUrl);
        }
        if (eventDataFromUrl) {
          sessionStorage.setItem("eventData", JSON.stringify(eventDataFromUrl));
        }
        sessionStorage.setItem("isLoggedIn", "true");
        setIsLoggedIn(true);
        // Remove the parameters from the URL after processing
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    } catch (error) {
      console.error("Unexpected error in useEffect:", error);
    }
  }, []);

  // If the user is logged in, render the LobbyPage instead of the login form
  if (isLoggedIn) {
    const storedEventData = sessionStorage.getItem("eventData");
    const parsedEventData = storedEventData
      ? JSON.parse(storedEventData)
      : eventData;
    return <LobbyPage sdkData={sdkData} eventData={parsedEventData} />; // Show the lobby page if logged in
  }

  return (
    <div>
      {loading ? (
        <Loader />
      ) : (
        <div className="container">
          <div className="row login-panel">
            <div className="col-md-12 login-form">
              <div className="col-md-6 content">
                <h1 className="attendee-welcome-text">Welcome</h1>
                <h2 className="inner-heading-des">
                   Log in to join the event
                </h2>
                <h2 className="meeting-des">{eventData?.title}</h2>
                <div className="event-details">
                  <div className="time-wrapper">
                    <img
                      className="calendar"
                      src={calendar}
                      alt="loading-img"
                    />
                    <div className="inner-heading">
                      <i className="far fa-calendar-alt"></i>{" "}
                      {dateTime.formattedDate}
                    </div>
                  </div>
                  <div className="inner-heading-inner">
                    <i className="far fa-clock"></i> {dateTime.formattedTime}{" "}
                    {dateTime.timeZone == "America/Detroit"
                      ? "America/New_York"
                      : dateTime.timeZone}
                  </div>
                  <div className="time-wrapper">
                    <img className="clock" src={clock} alt="loading-img" />
                    <div className="inner-heading">
                      <i className="fas fa-hourglass-start"></i>
                      {eventDuration}
                    </div>
                  </div>
                </div>
                {/* <div className='info-wrapper'>
                                        Join us as we delve into the strategies financial institutions can employ to navigate and capitalize on these uncertainties.
                                    </div>
                                    <div className='last-text-wrapper'>
                                        SPONSORED CONTENT FROM AMOUNT
                                    </div> */}
                <div className="event-description"></div>
              </div>
              <div className="col-md-6 content">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="form-group">
                    <label htmlFor="email" className="label-wrapper">
                      Enter the email you registered with.
                    </label>
                    <input
                      type="email"
                      className={`form-control ${errors.email ? "is-invalid" : ""
                        }`}
                      id="email"
                      placeholder="Email Address"
                      {...register("email", {
                        required: "Email is required",
                        pattern: {
                          value:
                            /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/,
                          message: "Enter a valid email address",
                        },
                      })}
                      aria-invalid={errors.email ? "true" : "false"}
                    />
                    {errors.email && (
                      <div className="invalid-feedback">
                        {getErrorMessage(errors.email)}
                      </div>
                    )}
                  </div>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="rememberMe"
                      checked={rememberMe}
                      onChange={handleRememberMeChange}
                    />
                    <label className="form-check-label" htmlFor="rememberMe">
                      Remember me
                    </label>
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={sitekey}
                      className="g-recaptcha"
                    />
                  </div>
                  <button type="submit" className="btn btn-primary w-100">
                    Join
                  </button>
                  <div className="last-section-form">
                    <p className="info-description-wrapper">
                      By submitting this form, I acknowledge that I have read and
                      understand TechDogs's{" "}
                      <a
                        className="blue-text-desci"
                        target="_blank"
                        rel="noopener noreferrer"
                        href="https://www.techdogs.com/privacy-policy"
                      >
                        Privacy Policy.
                      </a>
                    </p>
                    <p className="info-description-wrapper">This webinar uses Zoom Video Communications, Inc. technology. Connectivity could be impacted by your organization’s firewall.</p>
                    <p className="details-info-wrapper">
                      Need help?{" "}
                      <a
                        className="blue-text"
                        href="https://www.techdogs.com/contact-us"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Contact us
                      </a>
                    </p>

                    {/* <p className="details-info-wrapper">
                      Not registered?{" "}
                      <a
                        className="blue-text"
                        target="https://www.arizent.com/privacypolicy?_gl=1*1nno5cd*_gcl_au*MTYyOTE3MzEzOC4xNzI1MjYxNzM2"
                        href={eventData?.registrationUrl}
                      >
                        Register now
                      </a>
                    </p> */}
                    <div className="col-md-12 mt-3" style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                      <span style={{ color: "#000000", fontSize: "14px", marginRight: "8px", fontWeight: "bold" }}>Powered by</span>
                      <img src={process.env.PUBLIC_URL + '/techdogs-logo.webp'} alt="TechDogs Logo" style={{ height: "30px" }} />
                    </div>
                  </div>
                </form>
              </div>
              
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendeeLoginForm;
